import React, { useState } from 'react';
import { motion } from 'framer-motion';

// Add this section to replace the existing values grid in your HomePage component
const CyberpunkValuesGrid = ({ values }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <>
      <style>{`
        @keyframes value-data-flow {
          0% { transform: translateY(-100%); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translateY(100%); opacity: 0; }
        }
        
        @keyframes value-scan-horizontal {
          0% { left: -100%; }
          100% { left: 200%; }
        }
        
        @keyframes value-corner-pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        
        @keyframes value-border-glow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        
        .value-data-stream {
          position: absolute;
          width: 2px;
          height: 20px;
          background: linear-gradient(to bottom, transparent, #00ffff, transparent);
          animation: value-data-flow 2.5s linear infinite;
        }
        
        .value-scan-line {
          position: absolute;
          top: 50%;
          width: 100px;
          height: 1px;
          background: linear-gradient(90deg, transparent, #00ffff, transparent);
          animation: value-scan-horizontal 4s linear infinite;
        }
        
        .value-cyber-clip {
          clip-path: polygon(
            0 0, calc(100% - 16px) 0, 100% 16px,
            100% 100%, 16px 100%, 0 calc(100% - 16px)
          );
        }
        
        .value-icon-clip {
          clip-path: polygon(
            0 6px, 6px 0, calc(100% - 6px) 0, 100% 6px,
            100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0 calc(100% - 6px)
          );
        }
        
        .value-holographic-border {
          position: relative;
        }
        
        .value-holographic-border::before {
          content: '';
          position: absolute;
          inset: 0;
          padding: 2px;
          background: linear-gradient(45deg, #00ffff, #ff00ff, #ffff00, #00ffff);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          animation: value-border-glow 2s ease-in-out infinite;
          clip-path: polygon(
            0 0, calc(100% - 16px) 0, 100% 16px,
            100% 100%, 16px 100%, 0 calc(100% - 16px)
          );
        }
        
        .value-corner-accent {
          position: absolute;
          width: 20px;
          height: 20px;
          border: 2px solid #00ffff;
          animation: value-corner-pulse 2s ease-in-out infinite;
        }
        
        .value-corner-tl { top: -1px; left: -1px; border-right: none; border-bottom: none; }
        .value-corner-tr { top: -1px; right: -1px; border-left: none; border-bottom: none; }
        .value-corner-bl { bottom: -1px; left: -1px; border-right: none; border-top: none; }
        .value-corner-br { bottom: -1px; right: -1px; border-left: none; border-top: none; }
        
        .value-title-glow {
          text-shadow: 
            0 0 2px currentColor,
            0 0 4px currentColor;
        }
        
        .value-grid-bg {
          background-image: 
            linear-gradient(rgba(0, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 255, 255, 0.05) 1px, transparent 1px);
          background-size: 24px 24px;
        }
      `}</style>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
        {values.map((value, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            className="relative bg-black/90 backdrop-blur-xl border-2 border-cyan-500/30 hover:border-cyan-500 transition-all duration-500 p-6 group value-cyber-clip value-holographic-border overflow-hidden"
            style={{
              boxShadow: hoveredIndex === index
                ? '0 0 30px rgba(0, 255, 255, 0.3), inset 0 0 30px rgba(0, 255, 255, 0.1)'
                : '0 0 15px rgba(0, 255, 255, 0.1), inset 0 0 15px rgba(0, 255, 255, 0.05)'
            }}
          >
            {/* Corner Accents */}
            <div className="value-corner-accent value-corner-tl" />
            <div className="value-corner-accent value-corner-tr" />
            <div className="value-corner-accent value-corner-bl" />
            <div className="value-corner-accent value-corner-br" />

            {/* Scan Line */}
            <div className="value-scan-line" />

            {/* Data Flow Effects */}
            <div className="value-data-stream" style={{ left: '15%', animationDelay: '0s' }} />
            <div className="value-data-stream" style={{ left: '85%', animationDelay: '1.2s' }} />

            {/* Grid Overlay */}
            <div className="absolute inset-0 value-grid-bg opacity-20 pointer-events-none" />

            {/* Index Number */}
            <div className="absolute top-3 right-3 text-[10px] font-mono text-cyan-500/40 uppercase">
              #{String(index + 1).padStart(2, '0')}
            </div>

            {/* Icon Container */}
            <motion.div
              whileHover={{ scale: 1.1, rotate: 5 }}
              className="inline-flex p-3 border-2 border-cyan-500/40 mb-5 group-hover:border-cyan-500 transition-all duration-300 value-icon-clip relative bg-gradient-to-br from-cyan-500/10 to-purple-500/10"
              style={{
                boxShadow: hoveredIndex === index
                  ? '0 0 20px rgba(0, 255, 255, 0.5), inset 0 0 10px rgba(0, 255, 255, 0.2)'
                  : '0 0 10px rgba(0, 255, 255, 0.2)'
              }}
            >
              <value.icon className="h-7 w-7 text-cyan-400" />
              
              {/* Icon corner dots */}
              <div className="absolute top-0 left-0 w-1.5 h-1.5 bg-cyan-500" style={{ boxShadow: '0 0 4px #00ffff' }} />
              <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-purple-500" style={{ boxShadow: '0 0 4px #ff00ff' }} />
              <div className="absolute bottom-0 left-0 w-1.5 h-1.5 bg-yellow-500" style={{ boxShadow: '0 0 4px #ffff00' }} />
              <div className="absolute bottom-0 right-0 w-1.5 h-1.5 bg-cyan-500" style={{ boxShadow: '0 0 4px #00ffff' }} />
            </motion.div>

            {/* Title */}
            <h3 className="text-xl md:text-2xl font-heading font-bold mb-3 text-cyan-400 value-title-glow">
              {value.title}
            </h3>

            {/* Description */}
            <p className="text-sm md:text-base text-cyan-100/80 leading-relaxed font-mono">
              {value.description}
            </p>

            {/* Status Bar */}
            <div className="mt-4 pt-3 border-t border-cyan-500/20 flex items-center justify-between">
              <div className="flex items-center space-x-1.5">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" style={{ boxShadow: '0 0 4px #00ff00' }} />
                <span className="text-[10px] font-mono text-green-500/70 uppercase">Active</span>
              </div>
              <div className="flex space-x-1">
                <div className="w-1 h-3 bg-cyan-500/50" />
                <div className="w-1 h-4 bg-cyan-500/70" />
                <div className="w-1 h-2 bg-cyan-500/30" />
              </div>
            </div>

            {/* Holographic Hover Effect */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: hoveredIndex === index ? 1 : 0 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(circle at 50% 50%, rgba(0, 255, 255, 0.1), transparent 60%)'
              }}
            />
          </motion.div>
        ))}
      </div>
    </>
  );
};

// Export the component
export default CyberpunkValuesGrid;

// USAGE IN YOUR HOMEPAGE:
// Replace the existing values grid section with:
// <CyberpunkValuesGrid values={values} />